import { Box, Chip, Grid, Stack, styled, Typography } from '@mui/joy';
import { css } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Fragment } from 'react';
import { useForm } from 'react-hook-form';

import { BackendContainer } from '@/components/backend/BackendContainer';
import type { DashboardFilterFormData } from '@/components/backend/dashboard/DashboardFilter';
import { DashboardFilter } from '@/components/backend/dashboard/DashboardFilter';
import { HeatMap } from '@/components/backend/dashboard/HeatMap';
import { StatusCount } from '@/components/backend/dashboard/StatusCount';
import { StatusSankey } from '@/components/backend/dashboard/StatusSankey';
import { I18N_DE } from '@/i18n/de';
import { makeMetaTitle } from '@/modules/META';
import { DashboardTimePeriod, ReportMainCategory } from '@/modules/api';
import {
  backendApiAdminLevelsFindOneOptions,
  backendApiDashboardAnalyzeOptions,
} from '@/modules/api/@tanstack/react-query.gen';
import { useReportCategoriesQuery } from '@/queries/useReportCategoriesQuery';

const SectionHeader = styled(Typography)(
  ({ theme }) => css`
    color: ${theme.vars.palette.primary[500]};
    text-transform: uppercase;
    font-weight: 700;
    border-bottom: 2px solid ${theme.vars.palette.primary[500]};
  `,
);

export const Route = createFileRoute('/verwaltung/{-$adminLevelId}/dashboard')({
  head: async ({
    params: { adminLevelId },
    match: {
      context: { queryClient },
    },
  }) => {
    if (adminLevelId) {
      const adminLevel = await queryClient.ensureQueryData(
        backendApiAdminLevelsFindOneOptions({ path: { adminLevelId } }),
      );

      return {
        meta: [makeMetaTitle([`Dashboard ${adminLevel.name}`])],
      };
    }

    return {
      meta: [makeMetaTitle(['Dashboard'])],
    };
  },

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const adminLevelId = useParams({
    strict: false,
    select: (params) => params.adminLevelId,
  });
  const { data: adminLevel } = useQuery({
    ...backendApiAdminLevelsFindOneOptions({ path: { adminLevelId: adminLevelId ?? '' }, query: { children: true } }),
    enabled: !!adminLevelId,
  });

  const adminLevelIds: string[] = [
    ...(adminLevel?.id ? [adminLevel.id] : []),
    ...(adminLevel?.children?.filter((child) => child.managedByParent).map((child) => child.id) ?? []),
  ];

  const { data: categories } = useReportCategoriesQuery();

  const { control, watch, setValue } = useForm<DashboardFilterFormData>({
    defaultValues: { categoryIds: [], timePeriod: DashboardTimePeriod.LAST_7_DAYS },
  });

  const { data } = useQuery({
    ...backendApiDashboardAnalyzeOptions({
      body: {
        adminLevelId: adminLevel?.id,
        categoryIds: watch('categoryIds'),
        timePeriod: watch('timePeriod'),
      },
    }),
    refetchInterval: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const selectedCategories = watch('categoryIds');

  return (
    <BackendContainer>
      <Grid container spacing={2}>
        <Grid xs={4}>
          <Typography level="h3">Dashboard</Typography>
        </Grid>
        <Grid xs={8}>
          <Stack gap={1}>
            <DashboardFilter adminLevel={adminLevel} control={control} setValue={setValue} />
          </Stack>
        </Grid>
        {Object.values(ReportMainCategory).map((mainCategory) => {
          const mainCategoryGroup = categories?.filter(
            (category) => selectedCategories.includes(category.id) && category.mainCategory === mainCategory,
          );

          return mainCategoryGroup && mainCategoryGroup.length > 0 ? (
            <Fragment key={mainCategory}>
              <Stack alignItems="end" component={Grid} xs={4}>
                <Typography level="body-xs" sx={{ textTransform: 'uppercase', marginTop: 0.5 }}>
                  {I18N_DE.ReportMainCategory[mainCategory]}
                </Typography>
              </Stack>

              <Stack alignItems="center" component={Grid} direction="row" flexWrap="wrap" gap={1} xs={8}>
                {mainCategoryGroup?.map((category) => (
                  <Chip key={category.id} color="primary">
                    {category?.title}
                  </Chip>
                ))}
              </Stack>
            </Fragment>
          ) : null;
        })}

        <Grid md={6} xl={4} xs={12}>
          {data ? (
            <Stack gap={2}>
              <SectionHeader>Anzahl der Meldungen</SectionHeader>
              <StatusCount
                reportLikes={data.reportLikes}
                reportStatuses={data.reportStatuses}
                timePeriod={watch('timePeriod')}
              />
              <Typography level="title-sm">
                Anzahl zum Ende des ausgewählten Zeitraumes und Abweichung zum vorherigen Zeitraum
              </Typography>
            </Stack>
          ) : null}
        </Grid>
        <Grid md={6} xl={4} xs={12}>
          <Stack gap={2}>
            <SectionHeader>Bearbeitungsverlauf</SectionHeader>
            {data ? (
              <Box sx={{ height: '500px' }}>
                <StatusSankey reportChanges={data.reportChanges} />
              </Box>
            ) : null}
            <Typography level="title-sm">Statusänderungen im Zeitraum</Typography>
          </Stack>
        </Grid>
        <Grid md={6} xl={4} xs={12}>
          <Stack gap={2}>
            <SectionHeader>Heatmap</SectionHeader>

            <Box sx={{ height: '500px' }}>
              <HeatMap
                adminLevelIds={adminLevelIds}
                filter={{
                  categoryIds: watch('categoryIds'),
                  timePeriod: watch('timePeriod'),
                }}
              />
            </Box>

            <Typography level="title-sm">Im Zeitraum gemeldete Meldungen nach Erstellungszeitpunkt</Typography>
          </Stack>
        </Grid>
      </Grid>
    </BackendContainer>
  );
}
